import * as Sentry from "@sentry/react";

// A short, per-tab trail survives Google's redirect. It is evidence, never auth state.
const KEY = "pm_login_diagnostic";
let attempt;
try {
  const saved = JSON.parse(sessionStorage.getItem(KEY));
  if (saved?.id && Array.isArray(saved.steps) && Date.now() - saved.started < 30 * 60 * 1000) attempt = saved;
} catch { /* Storage may be unavailable in the failing browser. */ }

const current = () => {
  if (!attempt) {
    let product = "tax";
    try { if (localStorage.getItem("postLoginRedirect")?.startsWith("/ask")) product = "ask"; } catch { /* Keep memory-only diagnostics. */ }
    attempt = { id: crypto.randomUUID(), started: Date.now(), steps: [], product };
  }
  return attempt;
};

export const authAttemptId = () => {
  return attempt && !attempt.finished ? attempt.id : "";
};

export const authPageOpened = () => {
  if (attempt?.finished) attempt = null;
  authStep("auth_page_opened", { path: location.pathname });
};

export const authDiagnostic = () => {
  try {
    const widget = document.querySelector("[data-turnstile-widget] iframe");
    const rect = widget?.getBoundingClientRect();
    const inViewport = !!rect && rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 && rect.top < innerHeight && rect.left < innerWidth;
    const hit = inViewport ? document.elementFromPoint(
      Math.max(0, Math.min(innerWidth - 1, rect.x + rect.width / 2)),
      Math.max(0, Math.min(innerHeight - 1, rect.y + rect.height / 2)),
    ) : null;
    return {
      ...current(), steps: [...current().steps], captured: Date.now(),
      path: location.pathname, browser: navigator.userAgent, online: navigator.onLine,
      visibility: document.visibilityState, release: Sentry.getClient()?.getOptions().release,
      replayId: Sentry.getReplay()?.getReplayId(),
      widget: { present: !!widget, inViewport, coveredAtCenter: inViewport ? hit !== widget : null },
    };
  } catch { return { id: attempt?.id, steps: attempt?.steps || [] }; }
};

export const authStep = (step, details = {}, level) => {
  try {
    const a = current();
    a.steps.push({ step, at: Date.now(), ms: Date.now() - a.started, ...details });
    a.steps = a.steps.slice(-80);
    try { sessionStorage.setItem(KEY, JSON.stringify(a)); } catch { /* Best effort. */ }
    Sentry.addBreadcrumb({ category: "login", message: step, data: { attempt_id: a.id, ...details } });
    if (level) {
      const snapshot = authDiagnostic();
      Sentry.captureMessage(`Login diagnostic: ${step}`, {
        level, tags: { login_attempt: a.id, product: a.product, login_stage: step },
        // Sentry's default normalization depth would otherwise collapse step objects.
        extra: { login: { ...snapshot, steps: JSON.stringify(snapshot.steps) } },
      });
      if (level === "warning" || level === "error") {
        window.dispatchEvent(new Event("login-diagnostic-available"));
      }
    }
  } catch { /* Diagnostics must not stop sign-in. */ }
};

export const beginAuthAttempt = (method) => {
  attempt = null;
  authStep("sign_in_started", { method }, "info");
};

export const watchAuthStep = (step, details = {}) => {
  authStep(`${step}_started`, details);
  const timer = setTimeout(() => authStep(`${step}_still_waiting`, details, "warning"), 30000);
  return () => clearTimeout(timer);
};

// Capture the actual landed route, not merely a navigation request.
export const authLanded = (path) => {
  if (!attempt || attempt.finished) return;
  authStep("page_reached", { path });
  if (path.startsWith("/onboarding") || path.startsWith("/investing")) {
    attempt.finished = true;
    authStep("destination_reached", { path }, "info");
  }
};

export const installAuthDiagnostics = () => {
  const pagehide = () => {
    if (attempt && !attempt.finished) authStep("page_left", {}, "info");
  };
  const csp = (event) => {
    if (event.blockedURI?.includes("challenges.cloudflare.com")) {
      authStep("verification_blocked_by_csp", { directive: event.effectiveDirective }, "warning");
    }
  };
  window.addEventListener("pagehide", pagehide);
  document.addEventListener("securitypolicyviolation", csp);
  return () => {
    window.removeEventListener("pagehide", pagehide);
    document.removeEventListener("securitypolicyviolation", csp);
  };
};
